import { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { publish, subscribe, unsubscribe } from '../../EventsBus';
import { MediaDomainEvents } from '../domain';
import { VideoCallEvents } from '../../VideoCall/domain/events';
import { getFirstAudioDevice, getFirstCameraDevice, getFirstMicDevice, hasMicrophonePermissionEnabled, requestMediaPermissions, hasCameraPermissionEnabled, checkDeviceAvailability } from '../services/media';
import { useSessionStoragePersistedState } from '../../SessionStorageState';

export interface MediaContextValue {
	acceptedMediaPermissions: boolean,
	permissionsModalOpened: boolean,
	checkingPermissions: boolean,
	selectedVideoDevice: string | undefined,
	selectedMicDevice: string | undefined,
	selectedAudioDevice: string | undefined,
	noCameraDeviceFound: boolean,
	noMicDeviceFound: boolean,
	noAudioDeviceFound: boolean,
	noDevicesFoundModal: boolean,
	checkedMediaPermissions: boolean,
	devicesInUse: boolean,
	checkPermissions: () => void,
	toggleHowToActivatePermissions: () => void,
	requestPermissions: () => void,
	openNoDevicesFoundModal: () => void,
	closeNoDevicesFoundModal: () => void,
	retryDeviceAccess: () => void,
}

const MediaContext = createContext<MediaContextValue | undefined>(undefined);

export function MediaContextProvider({ children }: Readonly<{children: ReactElement}>) {
	const [acceptedMediaPermissions, setAcceptedMediaPermissions] = useState(false);
	const [permissionsModalOpened, setPermissionsModalOpened] = useState(false);
	const [checkingPermissions, setCheckingPermissions] = useState(true);
	const [checkedMediaPermissions, setCheckedMediaPermissions] = useState(false);
	const [selectedVideoDevice, setSelectedVideoDevice] = useSessionStoragePersistedState<string | undefined>('selectedVideoDevice', undefined);
	const [selectedMicDevice, setSelectedMicDevice] = useSessionStoragePersistedState<string | undefined>('selectedMicDevice', undefined);
	const [selectedAudioDevice, setSelectedAudioDevice] = useSessionStoragePersistedState<string | undefined>('selectedAudioDevice', undefined);
	const [howToActivatePermissionsModal, setHowToActivatePermissionsModal] = useState(false);
	const [noDevicesFoundModal, setNoDevicesFoundModal] = useState(false);
	const [devicesInUse, setDevicesInUse] = useState(false);

	const resetState = useCallback(() => {
		setAcceptedMediaPermissions(false);
		setPermissionsModalOpened(false);
		setCheckingPermissions(true);
		setCheckedMediaPermissions(false);
		setSelectedVideoDevice(undefined);
		setSelectedMicDevice(undefined);
		setSelectedAudioDevice(undefined);
		setHowToActivatePermissionsModal(false);
		setNoDevicesFoundModal(false);
		setDevicesInUse(false);
	}, []);

	const requestPermissions = useCallback(() => {
		setPermissionsModalOpened(false);
		publish(MediaDomainEvents.permissionsRequested);
	}, []);

	const permissionsRequested = useCallback(() => {
		requestMediaPermissions();
	}, []);

	const checkPermissions = useCallback(async () => {
		publish(MediaDomainEvents.checkingMediaPermissions);

		const isCamEnabled = await hasCameraPermissionEnabled();
		const isMicEnabled = await hasMicrophonePermissionEnabled();

		setCheckedMediaPermissions(true);
		publish(MediaDomainEvents.checkedMediaPermissions);

		if (!isCamEnabled && !isMicEnabled) {
			setPermissionsModalOpened(true);
			publish(MediaDomainEvents.notAcceptedMediaPermissions);
		} else {
			acceptedPermissions();
			setPermissionsModalOpened(false);
			publish(MediaDomainEvents.alreadyAcceptedMediaPermissions);
		}
		setCheckingPermissions(false);
	}, []);

	const setupSelectedDevices = useCallback(async () => {
		try {
			const availability = await checkDeviceAvailability();

			if (!availability.camera || !availability.microphone) {
				setDevicesInUse(true);
				return;
			}

			const camDevice = await getFirstCameraDevice();
			const micDevice = await getFirstMicDevice();
			const audioDevice = await getFirstAudioDevice();

			if (!camDevice) {
				publish(MediaDomainEvents.noCameraDevice);
			} else {
				setSelectedVideoDevice(camDevice?.deviceId);
			}

			if (!micDevice) {
				publish(MediaDomainEvents.noMicDevice);
			} else {
				setSelectedMicDevice(micDevice?.deviceId);
			}

			if (!audioDevice) {
				publish(MediaDomainEvents.noAudioDevice);
			} else {
				setSelectedAudioDevice(audioDevice?.deviceId);
			}

		} catch (error) {
			setDevicesInUse(true);
		}
	}, []);

	const acceptedPermissions = useCallback(() => {
		setAcceptedMediaPermissions(true);
		if (!selectedVideoDevice && !selectedMicDevice && !selectedAudioDevice) {
			setupSelectedDevices();
		}
	}, [selectedVideoDevice, selectedMicDevice, selectedAudioDevice, setupSelectedDevices]);

	const notAcceptedPermissions = useCallback(() => {
		setAcceptedMediaPermissions(false);
	}, []);

	const changeMicDevice = useCallback((event: CustomEvent<{ deviceId: string }>) => {
		setSelectedMicDevice(event.detail?.deviceId);
	}, []);

	const changeCamDevice = useCallback((event: CustomEvent<{ deviceId: string }>) => {
		setSelectedVideoDevice(event.detail?.deviceId);
	}, []);

	const changeAudioOutputDevice = useCallback((event: CustomEvent<{ deviceId: string }>) => {
		setSelectedAudioDevice(event.detail?.deviceId);
	}, []);

	const openNoDevicesFoundModal = useCallback(() => {
		setNoDevicesFoundModal(true);
	}, []);

	const closeNoDevicesFoundModal = useCallback(() => {
		setNoDevicesFoundModal(false);
	}, []);

	const retryDeviceAccess = useCallback(() => {
		setDevicesInUse(false);
		setupSelectedDevices();
	}, [setupSelectedDevices]);

	const handlePublisherDevicesInUse = useCallback(() => {
		setDevicesInUse(true);
	}, []);

	const noCameraDeviceFound = useMemo(() => {
		return acceptedMediaPermissions && !selectedVideoDevice;
	}, [acceptedMediaPermissions, selectedVideoDevice]);

	const noMicDeviceFound = useMemo(() => {
		return acceptedMediaPermissions && !selectedMicDevice;
	}, [acceptedMediaPermissions, selectedMicDevice]);

	const noAudioDeviceFound = useMemo(() => {
		return acceptedMediaPermissions && !selectedAudioDevice;
	}, [acceptedMediaPermissions, selectedAudioDevice]);

	const toggleHowToActivatePermissions = useCallback(() => {
		setHowToActivatePermissionsModal(!howToActivatePermissionsModal);
	}, [howToActivatePermissionsModal]);

	useEffect(() => {
		if (permissionsModalOpened) {
			publish(MediaDomainEvents.permissionsModalOpened);
		}
	}, [permissionsModalOpened]);

	useEffect(() => {
		subscribe(MediaDomainEvents.permissionsRequested, permissionsRequested);
		subscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedPermissions);
		subscribe(MediaDomainEvents.notAcceptedMediaPermissions, notAcceptedPermissions);

		subscribe(MediaDomainEvents.deniedMediaPermissions, notAcceptedPermissions);
		subscribe(MediaDomainEvents.blockedMediaPermissions, notAcceptedPermissions);

		subscribe(MediaDomainEvents.changeMicDevice, changeMicDevice);
		subscribe(MediaDomainEvents.changeCamDevice, changeCamDevice);
		subscribe(MediaDomainEvents.changeAudioOutputDevice, changeAudioOutputDevice);

		subscribe(MediaDomainEvents.noCameraDevice, openNoDevicesFoundModal);
		subscribe(MediaDomainEvents.noMicDevice, openNoDevicesFoundModal);
		subscribe(MediaDomainEvents.noAudioDevice, openNoDevicesFoundModal);

		subscribe(VideoCallEvents.publisherDevicesInUse, handlePublisherDevicesInUse);

		return () => {
			unsubscribe(MediaDomainEvents.permissionsRequested, permissionsRequested);
			unsubscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedPermissions);
			unsubscribe(MediaDomainEvents.notAcceptedMediaPermissions, notAcceptedPermissions);
			unsubscribe(MediaDomainEvents.deniedMediaPermissions, notAcceptedPermissions);
			unsubscribe(MediaDomainEvents.blockedMediaPermissions, notAcceptedPermissions);
			unsubscribe(MediaDomainEvents.changeCamDevice, changeCamDevice);
			unsubscribe(MediaDomainEvents.changeMicDevice, changeMicDevice);
			unsubscribe(MediaDomainEvents.changeAudioOutputDevice, changeAudioOutputDevice);
			unsubscribe(MediaDomainEvents.noCameraDevice, openNoDevicesFoundModal);
			unsubscribe(MediaDomainEvents.noMicDevice, openNoDevicesFoundModal);
			unsubscribe(MediaDomainEvents.noAudioDevice, openNoDevicesFoundModal);
			unsubscribe(VideoCallEvents.publisherDevicesInUse, handlePublisherDevicesInUse);
			resetState();
		};
	}, []);

	const value = useMemo(() => {
		return {
			acceptedMediaPermissions,
			permissionsModalOpened,
			checkingPermissions,
			selectedVideoDevice,
			selectedMicDevice,
			selectedAudioDevice,
			noCameraDeviceFound,
			noMicDeviceFound,
			noAudioDeviceFound,
			noDevicesFoundModal,
			checkedMediaPermissions,
			devicesInUse,
			checkPermissions,
			openNoDevicesFoundModal,
			closeNoDevicesFoundModal,
			toggleHowToActivatePermissions,
			requestPermissions,
			retryDeviceAccess
		};
	}, [acceptedMediaPermissions, permissionsModalOpened, checkingPermissions, selectedVideoDevice, selectedMicDevice, selectedAudioDevice, noCameraDeviceFound, noMicDeviceFound, noAudioDeviceFound, noDevicesFoundModal, checkedMediaPermissions, devicesInUse]);

	return (
		<MediaContext.Provider value={value}>
			{children}
		</MediaContext.Provider>
	);
}

export const useMediaContext = () => {
	const mediaContext = useContext(MediaContext);
	if (mediaContext === undefined) {
		throw new Error('useMediaContext must be inside a MediaContextProvider');
	}

	return mediaContext;
};
